'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import styles from './FadeSection.module.css';

interface FadeSectionProps {
  children: React.ReactNode;
  direction?: 'up' | 'down' | 'left' | 'right';
  delay?: number;
  threshold?: number;
  className?: string;
  sx?: React.CSSProperties;
}

export function FadeSection({
  children,
  direction = 'up',
  delay = 0,
  threshold = 0.1,
  className,
  sx,
}: FadeSectionProps) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return (
    <div
      ref={ref}
      className={cn(
        styles.fadeSection,
        styles[`fade-${direction}`],
        isVisible && styles.visible,
        className
      )}
      style={{ transitionDelay: `${delay}ms`, ...sx }}
    >
      {children}
    </div>
  );
}